import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "../../../../../../components/Input";
import { useEffect, useMemo, useState } from "react";
import theme from "../../../../../../theme";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import FileUpload from "../../../../../../components/FileUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { getDocument } from "../../../../../../services/dashboard";
import { useUserStore } from "../../../../../../store/useUserStore";

export default function DetailsOfDeceasedForm({
  detailOfDeceased,
  iceClaimNumber,
  onDeceasedCommentChange,
  documentData: initialDocumentData,
  isAssigned,
  claimStatus,
  deceasedComment,
  commentData,
  isEditable,
  handleDeceasedFieldChange,
}: any) {
  const [openPreview, setOpenPreview] = useState(false);
  const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const { relationOptions, loading } = useRelationOptions();
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});

  const addDocument = useClaimFormStore((state) => state.addDocument);
  const documentsFromStore = useClaimFormStore((state) => state.documents);
  const clearDocuments = useClaimFormStore((s) => s.clearDocuments);
  const role = useUserStore((state) => state.role);
  const stillBornFlag =
    detailOfDeceased?.is_still_born ??
    detailOfDeceased?.isStillBorn ??
    detailOfDeceased?.still_born ??
    detailOfDeceased?.stillBorn;
  const normalizedStillBornFlag = String(stillBornFlag ?? "")
    .trim()
    .toLowerCase();
  const normalizedCauseOfDeath = String(
    detailOfDeceased?.cause_of_death ?? detailOfDeceased?.causeOfDeath ?? "",
  )
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const gestationWeeks =
    detailOfDeceased?.gestation_weeks ?? detailOfDeceased?.gestationWeeks;
  const signsOfLifeAfterDelivery =
    detailOfDeceased?.signs_of_life_after_delivery ??
    detailOfDeceased?.signsOfLifeAfterDelivery;
  const intentionalTerminationOfPregnancy =
    detailOfDeceased?.intentional_termination_of_pregnancy ??
    detailOfDeceased?.intentionalTerminationOfPregnancy;
  const hasGestationDetails = gestationWeeks != null;
  const isStillBornClaim =
    stillBornFlag === true ||
    ["true", "yes", "1"].includes(normalizedStillBornFlag) ||
    ["stillbirth", "stillborn"].includes(normalizedCauseOfDeath) ||
    hasGestationDetails;

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const REQUIRED_DECEASED_DOC_TYPES = [
    "DECEASED_ID",
    "MEDICAL_REPORT",
    "DEATH_CERTIFICATE",
    "OTHER_DOCUMENT",
    "DHA_1663_FORM",
    "DHA_1680_HOME_DEATH_FORM",
    "DECEASED_YOUNG_ADULT",
  ];

  const existingDocTypes = useMemo(() => {
    return new Set(initialDocumentData.map((doc: any) => doc.document_type));
  }, [initialDocumentData]);

  const missingDocTypes = useMemo(() => {
    return REQUIRED_DECEASED_DOC_TYPES.filter(
      (type) => !documents[type] || documents[type].length === 0,
    );
  }, [documents]);

  useEffect(() => {
    const grouped = initialDocumentData.reduce(
      (acc: Record<string, any[]>, doc: any) => {
        if (!acc[doc.document_type]) acc[doc.document_type] = [];
        acc[doc.document_type].push(doc);
        return acc;
      },
      {},
    );

    const deceasedDocTypes = [
      "DECEASED_ID",
      "MEDICAL_REPORT",
      "DEATH_CERTIFICATE",
      "OTHER_DOCUMENT",
      "DHA_1663_FORM",
      "DHA_1680_HOME_DEATH_FORM",
      "DECEASED_YOUNG_ADULT",
    ];

    const mergedDocuments: Record<string, any[]> = { ...grouped };

    Object.entries(documentsFromStore).forEach(([type, file]) => {
      if (deceasedDocTypes.includes(type)) {
        mergedDocuments[type] = [
          {
            file_name: file.name,
            file_url: URL.createObjectURL(file),
            isLocal: true,
          },
        ];
      }
    });

    setDocuments(mergedDocuments);
  }, [initialDocumentData, documentsFromStore]);

  const handleDeleteDocument = (type: string, index: number) => {
    setDocuments((prev) => {
      const updated = [...(prev[type] || [])];
      updated.splice(index, 1);
      return { ...prev, [type]: updated };
    });
  };

  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "DETAILS_OF_DECEASED",
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc.document_id);
        if (!res || res.status !== 200) {
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);

        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  useEffect(() => {
    if (!isEditable) {
      clearDocuments(); // clears drafts when exiting edit mode
    }
  }, [isEditable]);

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      {isStillBornClaim && (
        <Grid size={12} sx={{ mb: 2 }}>
          <Box sx={{
            p: 3,
            bgcolor: "#fffbeb",
            border: "1px solid #fef3c7",
            borderRadius: "12px",
            textAlign: "left"
          }}>
            <Typography variant="subtitle1" fontWeight={700} color="#b45309" mb={2}>
              Stillborn Claim Details
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  disabled
                  value={`${gestationWeeks || 0} Weeks`}
                  height={40}
                  width="100%"
                  label="Pregnancy Weeks"
                  backgroundColor="#fff"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  disabled
                  value={signsOfLifeAfterDelivery === false ? "No" : "Yes"}
                  height={40}
                  width="100%"
                  label="Signs of life after delivery"
                  backgroundColor="#fff"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  disabled
                  value={intentionalTerminationOfPregnancy === false ? "No" : "Yes"}
                  height={40}
                  width="100%"
                  label="Intentional termination of pregnancy"
                  backgroundColor="#fff"
                />
              </Grid>
            </Grid>

            {gestationWeeks != null && Number(gestationWeeks) < 26 && (
              <Box sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "#fee2e2",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "13px",
                fontWeight: 500
              }}>
                ⚠️ Warning: This claim has only {gestationWeeks} weeks of pregnancy (less than the standard 26 weeks required). It was created under validation bypass.
              </Box>
            )}
          </Box>
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={detailOfDeceased?.title}
          height={40}
          width="100%"
          label="Title"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleDeceasedFieldChange("title", e.target.value)
          }
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selected ? (
                selected
              ) : (
                <span style={{ color: "#aaa" }}>Select Title</span>
              ),
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  maxHeight: "300px",
                },
              },
            },
          }}
        >
          <MenuItem
            value="Adv"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            ADV
          </MenuItem>
          <MenuItem
            value="Dr"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            DR
          </MenuItem>
          <MenuItem
            value="Miss"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MISS
          </MenuItem>
          <MenuItem
            value="Mr"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MR
          </MenuItem>
          <MenuItem
            value="Mrs"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MRS
          </MenuItem>
          <MenuItem
            value="Ms"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MS
          </MenuItem>
          <MenuItem
            value="Prof"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            PROF
          </MenuItem>
          <MenuItem
            value="Rev"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            REV
          </MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={detailOfDeceased?.initials}
          height={40}
          width="100%"
          label="Initials"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleDeceasedFieldChange("initials", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={detailOfDeceased?.last_name}
          height={40}
          width="100%"
          label="Surname"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleDeceasedFieldChange("last_name", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={(() => {
            const rawFirstName = detailOfDeceased?.first_name || "";
            if (isStillBornClaim && rawFirstName.trim()) {
              return /^baby\s+of\b/i.test(rawFirstName.trim())
                ? rawFirstName
                : `Baby of ${rawFirstName.trim()}`;
            }
            return rawFirstName;
          })()}
          height={40}
          width="100%"
          label="First Name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleDeceasedFieldChange("first_name", e.target.value)
          }
        />
      </Grid>
      {!isStillBornClaim && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="date"
            fullWidth
            label="Date of Birth"
            variant="outlined"
            placeholder="DD-MM-YYYY"
            value={detailOfDeceased?.date_of_birth || ""}
            onChange={(e: any) => {
              const rawValue = e.target.value;
              handleDeceasedFieldChange("date_of_birth", rawValue);
            }}
            disabled={!isEditable}
            height={40}
            width="100%"
            backgroundColor={
              isEditable ? "background.default" : "background.paper"
            }
            inputProps={{
              max: todayLocal,
            }}
          />
        </Grid>
      )}

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          type="date"
          fullWidth
          label="Date of Death"
          variant="outlined"
          placeholder="DD-MM-YYYY"
          value={detailOfDeceased?.date_of_death || ""}
          onChange={(e: any) => {
            const rawValue = e.target.value;
            handleDeceasedFieldChange("date_of_death", rawValue);
          }}
          disabled={!isEditable}
          height={40}
          width="100%"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          inputProps={{
            max: todayLocal,
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          fullWidth
          label="Relationship to claimant"
          variant="outlined"
          value={detailOfDeceased?.relationship}
          onChange={(e: any) =>
            handleDeceasedFieldChange("relationship", e.target.value)
          }
          disabled={!isEditable}
          height={40}
          width="100%"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
        >
          <MenuItem disabled value="" sx={{ background: "background.default" }}>
            -Select Relationship-
          </MenuItem>

          {loading ? (
            <MenuItem disabled>Loading relationships...</MenuItem>
          ) : (
            relationOptions.map((relation: string, index: number) => (
              <MenuItem
                key={index}
                value={relation}
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {relation}
              </MenuItem>
            ))
          )}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={detailOfDeceased?.cause_of_death}
          height={40}
          width="100%"
          label="Cause of death"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleDeceasedFieldChange("cause_of_death", e.target.value)
          }
        />
      </Grid>
      {!isStillBornClaim && (
        <>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              disabled={!isEditable}
              value={detailOfDeceased?.identification_type}
              height={40}
              width="100%"
              label="Identification Type"
              backgroundColor="background.paper"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              disabled={!isEditable}
              value={
                detailOfDeceased?.identification_number
                  ? detailOfDeceased?.identification_number
                  : detailOfDeceased?.passport_number
              }
              height={40}
              width="100%"
              label="Identification Number"
              backgroundColor="background.paper"
            />
          </Grid>
        </>
      )}
      <Grid size={12}>
        <Grid container rowSpacing={3} columnSpacing={10}>
          {Object.entries(documents).map(
            ([type, docs]) =>
              docs.length > 0 && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box key={type} mt={2}>
                    <Typography fontWeight={600} textAlign="left" fontSize={18}>
                      {type
                        .replace(/_/g, " ")
                        .toLocaleLowerCase()
                        .replace(/^\w/, (c) => c.toUpperCase())}
                    </Typography>
                    {docs.map((doc: any, index: any) => (
                      <Box
                        key={index}
                        sx={{
                          background: "#F5EEE9",
                          color: "#FF0000",
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexGrow={1}
                        >
                          <PictureAsPdfIcon />
                          <Box>
                            <Typography fontSize={14}>
                              {doc.file_name}
                            </Typography>
                          </Box>
                        </Box>
                        {isEditable ? (
                          <IconButton
                            onClick={() => handleDeleteDocument(type, index)}
                            size="small"
                          >
                            <DeleteIcon sx={{ color: "#FF0000" }} />
                          </IconButton>
                        ) : (
                          <IconButton
                            onClick={() => handlePreview(doc)}
                            size="small"
                          >
                            <VisibilityIcon sx={{ color: "#EC6E00" }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {/* {isEditable && docs.length === 0 && (
                  <Box mt={1}>
                    <FileUpload
                      inputId={`upload-${type}`}
                      onChange={(e: any) => {
                        if (e.target.files.length > 0) {
                          const file = e.target.files[0];
                           if (file.size > 2 * 1024 * 1024) {
                            setFileErrors(prev => ({ ...prev, [type]: "File must be less than 2MB" }));
                            return;
                          }
                          setFileErrors(prev => ({ ...prev, [type]: "" }));
                          const newDoc = {
                            file_name: file.name,
                            file_url: URL.createObjectURL(file),
                            isLocal: true,
                          };
                          setDocuments((prev) => ({
                            ...prev,
                            [type]: [newDoc],
                          }));
                          addDocument(type, file);
                        }
                      }}
                    />
                     {fileErrors && (
                      <Typography color="error" fontSize={14}>
                       {fileErrors[type]}
                      </Typography>
                    )}
                  </Box>
                )} */}
                  </Box>
                </Grid>
              ),
          )}
          {role === "consultant" &&
            isEditable &&
            missingDocTypes.map((type) => (
              <Grid size={{ xs: 12, md: 4 }} key={type}>
                <Box mt={2}>
                  <Typography fontWeight={600} fontSize={18}>
                    {type
                      .replace(/_/g, " ")
                      .toLowerCase()
                      .replace(/^\w/, (c) => c.toUpperCase())}
                  </Typography>

                  <FileUpload
                    inputId={`upload-${type}`}
                    onChange={(e: any) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      if (file.size > 2 * 1024 * 1024) {
                        setFileErrors((prev) => ({
                          ...prev,
                          [type]: "File must be less than 2MB",
                        }));
                        return;
                      }

                      setFileErrors((prev) => ({ ...prev, [type]: "" }));

                      addDocument(type, file); // ✅ store in Zustand

                      setDocuments((prev) => ({
                        ...prev,
                        [type]: [
                          {
                            file_name: file.name,
                            file_url: URL.createObjectURL(file),
                            isLocal: true,
                          },
                        ],
                      }));
                    }}
                  />

                  {fileErrors[type] && (
                    <Typography color="error" fontSize={14}>
                      {fileErrors[type]}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
        </Grid>
      </Grid>
      {comments?.length > 0 && (
        <Grid size={12} mt={4}>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" lineHeight={1.6}>
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}

      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={{ md: 12 }} mt={4}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={{ display: "block", textAlign: "left" }}
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={deceasedComment}
            onChange={onDeceasedCommentChange}
          />
        </Grid>
      )}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
